/**
 * Warehouse Details
 */

// React
import React, {useState, useEffect, useContext} from 'react';
import {useForm} from 'react-hook-form';

// Context
import AuthContext from '../../authContext';

// Redux
import {connect} from 'react-redux';
import {compose} from 'redux';
import {Store} from '../../redux/reducers';
import {
  getWarehouseWithIdAction,
  getWarehousesInventoryByIdActions,
} from '../warehouses/actions';

// Selector
import {createStructuredSelector} from 'reselect';
import {
  makeSelectWarehouse,
  makeSelectLoading,
  makeSelectError,
  makeSelectWarehouseInventory,
  makeSelectWarehouseInventoryCount,
} from '../warehouses/selectors';

//UI
import clsx from 'clsx';
import {makeStyles, createStyles, Theme} from '@material-ui/core/styles';
import {
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  ClickAwayListener,
  InputBase,
} from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';
import RenderFilter from '../../utils/listDaynamicFilter';

// Icon
import Icon from '@mdi/react';
import {
  mdiKeyOutline,
  mdiArrowRight,
  mdiCounter,
  mdiHomeCityOutline,
  mdiWarehouse,
  mdiMagnify,
  mdiArchiveOutline,
} from '@mdi/js';

// Component
import ListInventoryDetails from './components/listInventoryDetails';

// Helper
import {removeEmptyValueObject} from '../../utils/helpers';

// Type
import {SelectorPropsSkusInventory, PropsSkusInventory} from './interfaces';
import {PayloadInventoryById} from '../warehouses/interfaces';
import type {Filter} from '../../utils/listDaynamicFilter';


import handleError from "../../utils/handleError";
import handleNotfication from "../../utils/handleNotification";
import warehouseRepository from "client-sdk/src/Repository/warehouses-repository";

type Inputs = {
  'name.contains': string;
};

// Style
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& > *': {
        '@media (max-width: 768px)': {
          margin: theme.spacing(0),
        },
      },
    },
    paper: {
      marginTop: '36px',
      marginBottom: '36px',
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      border: '1px solid #ddd',
      borderRadius: '4px',
      '@media (max-width: 768px)': {
        padding: theme.spacing(0),
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(3),
        backgroundColor: theme.palette.background.default,
      },
    },

    item: {
      display: 'flex',
      alignSelf: 'center',
      alignItems: 'center',
      fontSize: '14px',
      fontWeight: 500,
    },
    icon: {
      marginRight: theme.spacing(1),
    },
    divider: {
      width: '98%',
      margin: '10px auto',
    },
    label: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '14px',
      fontWeight: 400,
      color: '#8A8A8A',
      marginRight: '10px',
    },
    headerBox: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '28px',
    },
    contentlabelTitle: {
      position: 'relative',
      color: theme.palette.gray.light,
      marginLeft: '40px',
      fontSize: '14px',
      fontWeight: 500,
      '&:before': {
        content: '"-"',
        position: 'absolute',
        left: '-20px',
      },
    },
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50vh',
      alignSelf: 'center',
      justifyContent: 'center',
      background: '#ccc',
    },
    changStatusIcon: {
      marginRight: '10px',
      position: 'absolute',
    },
    changStatusBox: {
      display: 'flex',
      textAlign: 'left',

      fontWeight: 400,
      fontSize: '14px',
    },
    renderValue: {
      paddingLeft: '32px',
    },
    radioGroup: {
      margin: '-8px 0',
    },
    radioGroupItem: {
      borderBottom: '1px solid #D8D8D8',
      marginRight: '-8px',
      padding: '8px 16px',
      '&:last-child': {
        borderBottom: '0px',
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'justify',
      lineHeight: 2,
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    editIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.blue.main,
      },
    },

    searchBox: {
      display: 'flex',
      position: 'relative',
      backgroundColor: theme.palette.common.white,
      borderRadius: '4px',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
    },

    input: {
      marginLeft: theme.spacing(1),
      flex: 2,
      '@media (max-width: 768px)': {
        fontSize: '12px',
      },
    },
    iconButton: {
      padding: 10,
      position: 'relative',
      '&:before': {
        content: '""',
        position: 'absolute',
        width: '1px',
        height: '36px',
        top: '4px',
        left: '-9px',
        backgroundColor: '#EDF1F4',
      },
    },
    filterBox: {
      position: 'absolute',
      backgroundColor: '#fff',
      width: '100%',
      top: '47px',
      right: '0',
      marginTop: '12px',
      borderRadius: '0  0 4px  4px',
      padding: '16px 16px',
      boxSizing: 'border-box',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
      zIndex: 10,
    },
    filterOpen: {
      display: 'block',
    },
    filterClose: {
      display: 'none',
    },
    magnifyIcon: {
      transform: 'rotate(-90deg)',
    },
  })
);

export function Details(props: PropsSkusInventory) {
  const authContext = useContext(AuthContext);

  const classes = useStyles();
  const [filters, setFilters] = useState([] as Filter[]);

  const [filtersValues, setFiltersValues] = useState({
    page: 0,
    size: 10,
  } as any);
  const [openSize, setOpenSize] = React.useState(false as boolean);
  const {register, handleSubmit} = useForm<Inputs>();
  const [openFilterBox, setOpenFilterBox] = useState(false as boolean);


    const [warehouseInventory, setWarehouseInventory] = React.useState([]);
    const [warehouseInventoryCount, setWarehouseInventoryCount] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [count, setCount] = React.useState(0);
    const [warehouse, setWarehouse] = React.useState({});
    const [error, setError] = React.useState("");

    const getWarehousesWithId=async(id:any)=>{
      const payload = {id};
        try {
          setLoading(true);
          const response = await warehouseRepository.getWarehouseWithId({payload});
          setWarehouse(response.data);
        } catch (error) {
          setError(error.message);
            handleError(error.response);
        }finally{
          setLoading(false);
        }
    }
    const getWarehousesInventoryWithId=async(actions:any)=>{
      const payload = {...actions};
        try {
          setLoading(true);
          const response =await warehouseRepository.getWarehousesInventoryById({payload});
          setWarehouseInventory(response.data.content);
          setWarehouseInventoryCount(response.data.totalElements);
        } catch (error) {
          setError(error.message);
            handleError(error.response);
        }finally{
          setLoading(false);
        }
    }

  useEffect(() => {
    const {warehousesId} = props.match.params;
    getWarehousesWithId(warehousesId);
    getWarehousesInventoryWithId({
      id: warehousesId,
      pagination: filtersValues,
    });
    setupFilters();

    // props.getWarehousesWithIdDispatch(id);
  }, [filtersValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setFiltersValues({...filtersValues, page: page - 1});
  };
  const handleChangeSizePage = (event: React.ChangeEvent<{value: unknown}>) => {
    setFiltersValues({...filtersValues, page: 0, size: event.target.value});
  };

  const handleClose = () => {
    setOpenSize(false);
  };

  const setupFilters = () => {
    setFilters([
      {
        type: 'input',
        id: 'name.contains',
        label: 'نام کالا',
        icon: mdiArchiveOutline,
      },
      {
        type: 'input',
        id: 'id.equals',
        label: 'شناسه کالا',
        icon: mdiKeyOutline,
      },
    ]);
  };

  const handleClick = () => {
    setOpenFilterBox(prev => !prev);
  };

  const handleClickAway = (e: any) => {
    // TODO: Shet
    if (
      e.target.getAttribute('dir') !== 'rtl' &&
      e.target.getAttribute('type') !== 'checkbox' &&
      e.target.getAttribute('class') !== 'PrivateSwitchBase-input-73' &&
      e.target.getAttribute('class') !==
        'MuiTypography-root MuiListItemText-primary MuiTypography-body1 MuiTypography-displayBlock' &&
      e.target.getAttribute('aria-hidden') !== 'true'
    )
      setOpenFilterBox(false);
  };

  const onSubmit = (filtervalues: any) => {
    setFiltersValues({
      ...removeEmptyValueObject(filtervalues),
      page: 0,
      size: 10,
    });
    setOpenFilterBox(false);
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={5}>
        <Grid item xs={12} sm={6}>
          <div className={classes.headerBox}>
            <Typography variant="h6" component="h2">
              {`
        جزيیات انبار
    ${warehouse.name}
     
        `}
            </Typography>
            <span className={classes.contentlabelTitle}>
              {props.match.params?.skuId}
            </span>
          </div>

          <Button
            aria-label="details"
            size="medium"
            onClick={() => window.history.back()}
          >
            <Icon path={mdiArrowRight} size={1} />
            {`بازگشت`}
          </Button>
        </Grid>

        <Grid item xs={12} sm={6}>
          <div className={classes.searchBox}>
            <form onSubmit={handleSubmit(onSubmit)} className={classes.input}>
              <IconButton
                type="submit"
                className={classes.iconButton}
                aria-label="search"
              >
                <Icon
                  path={mdiMagnify}
                  size={1}
                  className={classes.magnifyIcon}
                />
              </IconButton>
              <InputBase
                className={classes.input}
                name="name.contains"
                placeholder="نام کالا "
                inputRef={register({required: true})}
                inputProps={{'aria-label': 'search google maps'}}
              />
            </form>
            <ClickAwayListener onClickAway={handleClickAway}>
              <div>
                <Button
                  type="button"
                  onClick={handleClick}
                  className={classes.iconButton}
                  aria-label="search"
                >
                  فیلترها
                </Button>
                {openFilterBox ? (
                  <div
                    className={clsx(classes.filterBox, {
                      [classes.filterOpen]: openFilterBox,
                    })}
                  >
                    <RenderFilter
                      filters={filters}
                      onFilter={(filtervalues: any) => {
                        setFiltersValues({
                          ...filtervalues,
                          page: 0,
                          size: 10,
                          // partnerId: {
                          //   equals: props.match.params.warehousesId || null,
                          // },
                        });
                        setOpenFilterBox(false);
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </ClickAwayListener>
          </div>
        </Grid>
      </Grid>

      <Paper elevation={3} className={classes.paper} variant="elevation" square>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon path={mdiWarehouse} size={1} className={classes.icon} />
                {`نام انبار:  `}
              </span>

              <span>{warehouse.name}</span>
            </div>
          </Grid>
          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon path={mdiKeyOutline} size={1} className={classes.icon} />
                {`شناسه انبار:  `}
              </span>
              <span>{warehouse.id}</span>
            </div>
          </Grid>

          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon path={mdiCounter} size={1} className={classes.icon} />
                {`ظرفیت:  `}
              </span>
              <span>{warehouse.capacity}</span>
            </div>
          </Grid>

          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon
                  path={mdiHomeCityOutline}
                  size={1}
                  className={classes.icon}
                />
                {`والد انبار:  `}
              </span>
              <span>{warehouse.city}</span>
            </div>
          </Grid>
        </Grid>
      </Paper>

      <Grid container item spacing={6}>
        <Grid item xs={12}>
          {loading ? (
            <div className={classes.loading}>
              <CircularProgress />
            </div>
          ) : (
            warehouseInventory && (
              <ListInventoryDetails
                data={warehouseInventory}
                warehousesId={props.match.params?.warehousesId}
              />
            )
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {warehouseInventory && (
            <FormControl
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <div style={{marginLeft: '10px', color: '#8a8a8a'}}>
                {' '}
                تعداد نمایش در هر صفحه:
              </div>
              <Select
                open={openSize}
                onClose={handleClose}
                onOpen={() => setOpenSize(true)}
                value={filtersValues.size}
                onChange={handleChangeSizePage}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          )}
        </Grid>
        <Grid xs={12} sm={6} container item direction="row-reverse">
          {warehouseInventory && (
            <Pagination
              count={Math.ceil(
                warehouseInventoryCount / filtersValues.size
              )}
              color="secondary"
              page={filtersValues.page + 1}
              onChange={handleChangePage}
            />
          )}
        </Grid>
      </Grid>
    </div>
  );
}

// const mapStateToProps = createStructuredSelector<
//   Store,
//   SelectorPropsSkusInventory
// >({
//   warehouseInventory: makeSelectWarehouseInventory(),
//   warehouseInventoryCount: makeSelectWarehouseInventoryCount(),
//   warehouse: makeSelectWarehouse(),
//   loading: makeSelectLoading(),
//   error: makeSelectError(),
// });

// function mapDispatchToProps(dispatch: Function) {
//   return {
//     getWarehousesWithIdDispatch: (id: number) =>
//       dispatch(getWarehouseWithIdAction({id})),
//     getWarehousesInventoryWithIdDispatch: (payload: PayloadInventoryById) =>
//       dispatch(getWarehousesInventoryByIdActions(payload)),
//   };
// }

// const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default Details;
